import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, FileText, Building2, Phone, Mail, Printer, ArrowRight } from 'lucide-react';
import { ContactButtons } from './ContactButtons';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Paquete de cada tarjeta (mismo orden que tiers) -> /pedido?plan=...
const PLAN_KEYS = ['solo', 'pareja', 'familia'];

// Tarjeta de la foto de $12. Estuvo oculta el 2026-10-11 por un fallo de créditos
// de Snap2Pass; ya resuelto y probado de punta a punta el 2026-10-12. Para ocultarla: false.
const SHOW_PHOTO = true;
type Tier = {
  name: string;
  who: string;
  forms: string;
  price: string;
  save: string | null;
  extra: string | null;
  popular: boolean;
};
type PricingStrings = {
  heading: string;
  headingSub: string;
  talkTitle: string;
  talkBody: string;
  assistedTitle: string;
  assistedSub: string;
  tapHint: string;
  tiers: Tier[];
  includesTitle: string;
  includes: string[];
  getStarted: string;
  photoTitle: string;
  photoBody: string;
  photoCta: string;
  uspsTitle: string;
  uspsBody: string;
  uspsCta: string;
  uspsNote: string;
  transTitle: string;
  transBody: string;
  transCta: string;
  transNote: string;
  deliveryTitle: string;
  deliveryEmailTitle: string;
  deliveryEmailBody: string;
  deliveryPrintTitle: string;
  deliveryPrintBody: string;
  twoCostTitle: string;
  twoCostSub: string;
  noorFeeTitle: string;
  noorFeeDesc: string;
  govFeeTitle: string;
  govFeeDesc: string;
  govNoteBefore: string;
  govLink: string;
  govNoteAfter: string;
};
// EN/ES finalized. HT/VI = best effort, pending native review.
// 2026-10-18: se quitó de "includes" la línea "la misma lista de pasos también en audio"
// (ese audio no existe). Se agregó "Video explicativo paso a paso" (video "¿Cómo llenar su
// cheque?" ya creado). Mantener igual que components/PlanTip.tsx.
// 2026-10-23: el plazo pasó de "24–48 horas" a "menos de 24 horas" (decisión de Manuel).
const T: Record<Language, PricingStrings> = {
  en: {
    heading: 'Simple, transparent pricing',
    headingSub: 'One flat price for any form. You only pay Noor for the help — the U.S. government fee is always paid separately, directly to the government.',
    talkTitle: "Prefer to talk? We'll fill it out with you.",
    talkBody: "Call or message us on WhatsApp and we'll guide you through your form step by step, in your language. Asking which form you need is always free.",
    assistedTitle: 'We prepare it for you',
    assistedSub: 'We guide you, a specialist reviews it, and it arrives printed at your door.',
    tapHint: 'Tap a package to choose it.',
    tiers: [{
      name: 'Just me',
      who: 'For one person',
      forms: '1 person',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'The two of us',
      who: 'For a couple',
      forms: '2 people',
      price: '149',
      save: 'Save $49',
      extra: null,
      popular: false
    }, {
      name: 'My family',
      who: 'For a family',
      forms: 'Up to 4 people',
      price: '199',
      save: 'About $50 per person',
      extra: '+$40 per extra person',
      popular: false
    }],
    includesTitle: 'Every package includes:',
    includes: ['Guided by phone, WhatsApp, or online — in your language', 'Automatic error & completeness check', 'Reviewed personally by a Noor specialist', 'Every form your case needs — at no extra cost', 'Color-printed packet mailed to your home', 'A checklist of the steps to follow, included with your form', 'Step-by-step explainer video', 'Ready in less than 24 hours', 'Works for any passport form (new, renewal, minor, lost)'],
    getStarted: 'Get started',
    photoTitle: 'Passport photo (digital)',
    photoBody: 'Take your photo through our platform. We check it against the official rules and hand it back in digital format (2×2), ready to print or for your online renewal.',
    photoCta: 'Request my photo',
    uspsTitle: 'Already have your photo ready?',
    uspsBody: 'Next step: book your free appointment at the nearest USPS. We give you the approved photo — you pick the day.',
    uspsCta: 'Book your official appointment here',
    uspsNote: 'Noor Passport Services does not book appointments. We connect you directly with the official USPS system.',
    transTitle: 'Is your document in another language?',
    transBody: 'If your application needs a birth certificate or other document translated into English, you can get a certified translation online.',
    transCta: 'Translate my document',
    transNote: 'Noor does not translate documents; we connect you with a certified translation service. Noor may earn a commission, at no extra cost to you.',
    deliveryTitle: 'How you receive your form',
    deliveryEmailTitle: 'By email — free',
    deliveryEmailBody: 'Get your completed form as a PDF, ready to print and sign.',
    deliveryPrintTitle: 'Printed & mailed — optional',
    deliveryPrintBody: 'We print and ship it to you in color or black & white. Included free with every package (color).',
    twoCostTitle: 'Two separate costs — nothing hidden',
    twoCostSub: 'What you pay Noor, and what you pay the government.',
    noorFeeTitle: 'Noor service fee',
    noorFeeDesc: 'Help preparing your form',
    govFeeTitle: 'U.S. government fee',
    govFeeDesc: 'Paid per person, directly to the government',
    govNoteBefore: "Government fees are set by the government, vary by application type, and are paid per person when you submit — Noor never collects them. A family pays one Noor package plus each person's own government fee. See current rates and apply directly at ",
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  },
  es: {
    heading: 'Precios simples y transparentes',
    headingSub: 'Un solo precio fijo para cualquier formulario. A Noor le paga solo por la ayuda — la tarifa del gobierno de EE. UU. siempre se paga aparte, directamente al gobierno.',
    talkTitle: '¿Prefiere hablar? Lo llenamos con usted.',
    talkBody: 'Llámenos o escríbanos por WhatsApp y lo guiamos paso a paso con su formulario, en su idioma. Preguntar cuál formulario necesita siempre es gratis.',
    assistedTitle: 'Nosotros lo preparamos',
    assistedSub: 'Lo guiamos, un especialista lo revisa, y le llega impreso a su puerta.',
    tapHint: 'Toque un paquete para elegirlo.',
    tiers: [{
      name: 'Solo yo',
      who: 'Para una persona',
      forms: '1 persona',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'Los dos',
      who: 'Para una pareja',
      forms: '2 personas',
      price: '149',
      save: 'Ahorre $49',
      extra: null,
      popular: false
    }, {
      name: 'Mi familia',
      who: 'Para una familia',
      forms: 'Hasta 4 personas',
      price: '199',
      save: 'Unos $50 por persona',
      extra: '+$40 por persona adicional',
      popular: false
    }],
    includesTitle: 'Cada paquete incluye:',
    includes: ['Guiado por teléfono, WhatsApp o en línea — en su idioma', 'Revisión automática de errores y de que esté completo', 'Revisado personalmente por un especialista de Noor', 'Todos los formularios que su caso necesite — sin costo adicional', 'Paquete impreso a color y enviado a su casa', 'Una lista de los pasos a seguir, incluida junto con su formulario', 'Video explicativo paso a paso', 'Listo en menos de 24 horas', 'Sirve para cualquier formulario de pasaporte (nuevo, renovación, menor, perdido)'],
    getStarted: 'Comenzar',
    photoTitle: 'Foto de pasaporte (digital)',
    photoBody: 'Tome su foto a través de nuestra plataforma. Nosotros la revisamos según las reglas oficiales y se la entregamos en formato digital (2×2), lista para imprimir o para su renovación en línea.',
    photoCta: 'Solicitar mi foto',
    uspsTitle: '¿Ya tiene su foto lista?',
    uspsBody: 'Siguiente paso: agende su cita gratis en el USPS más cercano. Nosotros le damos la foto aprobada — usted elige el día.',
    uspsCta: 'Saque su cita oficial aquí',
    uspsNote: 'Noor Passport Services no agenda citas. Lo conectamos directo con el sistema oficial de USPS.',
    transTitle: '¿Su documento está en otro idioma?',
    transBody: 'Si su trámite necesita un acta de nacimiento u otro documento traducido al inglés, puede hacer su traducción certificada en línea.',
    transCta: 'Traducir mi documento',
    transNote: 'Noor no traduce documentos; lo conectamos con un servicio de traducción certificada. Noor puede recibir una comisión, sin costo adicional para usted.',
    deliveryTitle: 'Cómo recibe su formulario',
    deliveryEmailTitle: 'Por correo — gratis',
    deliveryEmailBody: 'Reciba su formulario completo en PDF, listo para imprimir y firmar.',
    deliveryPrintTitle: 'Impreso y enviado — opcional',
    deliveryPrintBody: 'Lo imprimimos y se lo enviamos a color o en blanco y negro. Incluido gratis en cada paquete (a color).',
    twoCostTitle: 'Dos costos separados — nada escondido',
    twoCostSub: 'Lo que le paga a Noor, y lo que le paga al gobierno.',
    noorFeeTitle: 'Tarifa de servicio de Noor',
    noorFeeDesc: 'Ayuda para preparar su formulario',
    govFeeTitle: 'Tarifa del gobierno de EE. UU.',
    govFeeDesc: 'Se paga por persona, directamente al gobierno',
    govNoteBefore: 'Las tarifas del gobierno las fija el gobierno, varían según el tipo de solicitud y se pagan por persona al momento de entregar — Noor nunca las cobra. Una familia paga un solo paquete de Noor más la tarifa del gobierno de cada persona. Vea las tarifas actuales y solicite directamente en ',
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  },
  ht: {
    heading: 'Pri senp e klè',
    headingSub: 'Yon sèl pri fiks pou nenpòt fòm. Ou peye Noor sèlman pou èd la — frè gouvènman ameriken an toujou peye apa, dirèkteman bay gouvènman an.',
    talkTitle: 'Ou pi renmen pale? N ap ranpli li ansanm ak ou.',
    talkBody: 'Rele oswa voye mesaj ban nou sou WhatsApp epi n ap gide ou etap pa etap nan fòm ou an, nan lang ou. Mande ki fòm ou bezwen toujou gratis.',
    assistedTitle: 'Nou prepare li pou ou',
    assistedSub: 'Nou gide ou, yon espesyalis tcheke li, epi li rive enprime devan pòt ou.',
    tapHint: 'Tape sou yon pakè pou chwazi li.',
    tiers: [{
      name: 'Sèlman mwen',
      who: 'Pou yon moun',
      forms: '1 moun',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'Nou de a',
      who: 'Pou yon koup',
      forms: '2 moun',
      price: '149',
      save: 'Ekonomize $49',
      extra: null,
      popular: false
    }, {
      name: 'Fanmi mwen',
      who: 'Pou yon fanmi',
      forms: 'Jiska 4 moun',
      price: '199',
      save: 'Anviwon $50 pa moun',
      extra: '+$40 pou chak moun anplis',
      popular: false
    }],
    includesTitle: 'Chak pakè gen ladan:',
    includes: ['Gidans pa telefòn, WhatsApp, oswa sou entènèt — nan lang ou', 'Tcheke otomatik pou erè ak pou wè si li konplè', 'Yon espesyalis Noor tcheke li pèsonèlman', 'Tout fòm ka ou a bezwen — san frè anplis', 'Pakè enprime an koulè voye lakay ou', 'Yon lis etap pou swiv, enkli ansanm ak fòm ou an', 'Videyo eksplikatif etap pa etap', 'Pare nan mwens pase 24 èdtan', 'Mache pou nenpòt fòm paspò (nouvo, renouvèlman, minè, pèdi)'],
    getStarted: 'Kòmanse',
    photoTitle: 'Foto paspò (dijital)',
    photoBody: 'Pran foto ou atravè platfòm nou an. Nou tcheke li selon règ ofisyèl yo epi nou remèt ou li an fòm dijital (2×2), pare pou enprime oswa pou renouvèlman ou sou entènèt.',
    photoCta: 'Mande foto mwen',
    uspsTitle: 'Ou deja gen foto ou pare?',
    uspsBody: 'Pwochen etap: pran randevou gratis ou nan biwo USPS ki pi pre a. Nou ba ou foto apwouve a — ou chwazi jou a.',
    uspsCta: 'Pran randevou ofisyèl ou isit la',
    uspsNote: 'Noor Passport Services pa pran randevou. Nou konekte ou dirèkteman ak sistèm ofisyèl USPS la.',
    transTitle: 'Èske dokiman ou an nan yon lòt lang?',
    transBody: 'Si demand ou an bezwen yon batìstè oswa yon lòt dokiman tradui an anglè, ou ka fè yon tradiksyon sètifye sou entènèt.',
    transCta: 'Tradui dokiman mwen',
    transNote: 'Noor pa tradui dokiman; nou konekte ou ak yon sèvis tradiksyon sètifye. Noor ka touche yon komisyon, san okenn frè anplis pou ou.',
    deliveryTitle: 'Kijan ou resevwa fòm ou',
    deliveryEmailTitle: 'Pa imèl — gratis',
    deliveryEmailBody: 'Jwenn fòm ou konplè an PDF, pare pou enprime ak siyen.',
    deliveryPrintTitle: 'Enprime epi voye — opsyonèl',
    deliveryPrintBody: 'Nou enprime li epi voye li ba ou an koulè oswa nwa e blan. Enkli gratis ak chak pakè (an koulè).',
    twoCostTitle: 'De depans separe — anyen kache',
    twoCostSub: 'Sa ou peye Noor, ak sa ou peye gouvènman an.',
    noorFeeTitle: 'Frè sèvis Noor',
    noorFeeDesc: 'Èd pou prepare fòm ou',
    govFeeTitle: 'Frè gouvènman ameriken an',
    govFeeDesc: 'Peye pa moun, dirèkteman bay gouvènman an',
    govNoteBefore: 'Se gouvènman an ki fikse frè yo, yo varye selon tip aplikasyon an, epi yo peye pa moun lè ou soumèt — Noor pa janm kolekte yo. Yon fanmi peye yon sèl pakè Noor plis frè gouvènman an pou chak moun. Gade tarif aktèl yo epi aplike dirèkteman sou ',
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  },
  vi: {
    heading: 'Giá đơn giản, minh bạch',
    headingSub: 'Một mức giá cố định cho mọi mẫu đơn. Quý vị chỉ trả cho Noor phần hỗ trợ — phí chính phủ Hoa Kỳ luôn được trả riêng, trực tiếp cho chính phủ.',
    talkTitle: 'Thích nói chuyện hơn? Chúng tôi sẽ điền cùng quý vị.',
    talkBody: 'Gọi hoặc nhắn tin cho chúng tôi qua WhatsApp và chúng tôi sẽ hướng dẫn quý vị từng bước với mẫu đơn, bằng ngôn ngữ của quý vị. Hỏi quý vị cần mẫu đơn nào luôn miễn phí.',
    assistedTitle: 'Chúng tôi chuẩn bị giúp quý vị',
    assistedSub: 'Chúng tôi hướng dẫn quý vị, một chuyên viên xem lại, và nó được in và gửi đến tận nhà.',
    tapHint: 'Nhấn vào một gói để chọn.',
    tiers: [{
      name: 'Chỉ mình tôi',
      who: 'Cho một người',
      forms: '1 người',
      price: '99',
      save: null,
      extra: null,
      popular: true
    }, {
      name: 'Hai chúng tôi',
      who: 'Cho một cặp',
      forms: '2 người',
      price: '149',
      save: 'Tiết kiệm $49',
      extra: null,
      popular: false
    }, {
      name: 'Gia đình tôi',
      who: 'Cho một gia đình',
      forms: 'Tối đa 4 người',
      price: '199',
      save: 'Khoảng $50 mỗi người',
      extra: '+$40 mỗi người thêm',
      popular: false
    }],
    includesTitle: 'Mỗi gói bao gồm:',
    includes: ['Hướng dẫn qua điện thoại, WhatsApp hoặc trực tuyến — bằng ngôn ngữ của quý vị', 'Tự động kiểm tra lỗi và tính đầy đủ', 'Được chuyên viên Noor đích thân xem lại', 'Mọi mẫu đơn trường hợp của quý vị cần — không tính thêm phí', 'Bộ hồ sơ in màu gửi đến nhà quý vị', 'Một danh sách các bước cần làm, kèm theo mẫu đơn của quý vị', 'Video hướng dẫn từng bước', 'Sẵn sàng trong vòng chưa đầy 24 giờ', 'Dùng cho mọi mẫu đơn hộ chiếu (mới, gia hạn, trẻ vị thành niên, bị mất)'],
    getStarted: 'Bắt đầu',
    photoTitle: 'Ảnh hộ chiếu (kỹ thuật số)',
    photoBody: 'Chụp ảnh của quý vị qua nền tảng của chúng tôi. Chúng tôi kiểm tra theo quy định chính thức và trả lại cho quý vị ở định dạng kỹ thuật số (2×2), sẵn sàng để in hoặc để gia hạn trực tuyến.',
    photoCta: 'Yêu cầu ảnh của tôi',
    uspsTitle: 'Đã có sẵn ảnh của quý vị?',
    uspsBody: 'Bước tiếp theo: đặt lịch hẹn miễn phí tại USPS gần nhất. Chúng tôi cung cấp ảnh đã được duyệt — quý vị chọn ngày.',
    uspsCta: 'Đặt lịch hẹn chính thức tại đây',
    uspsNote: 'Noor Passport Services không đặt lịch hẹn. Chúng tôi kết nối quý vị trực tiếp với hệ thống chính thức của USPS.',
    transTitle: 'Giấy tờ của quý vị bằng ngôn ngữ khác?',
    transBody: 'Nếu hồ sơ của quý vị cần giấy khai sinh hoặc giấy tờ khác được dịch sang tiếng Anh, quý vị có thể làm bản dịch có chứng nhận trực tuyến.',
    transCta: 'Dịch giấy tờ của tôi',
    transNote: 'Noor không dịch giấy tờ; chúng tôi kết nối quý vị với một dịch vụ dịch thuật có chứng nhận. Noor có thể nhận hoa hồng, không tính thêm phí cho quý vị.',
    deliveryTitle: 'Cách quý vị nhận mẫu đơn',
    deliveryEmailTitle: 'Qua email — miễn phí',
    deliveryEmailBody: 'Nhận mẫu đơn đã hoàn tất dưới dạng PDF, sẵn sàng để in và ký.',
    deliveryPrintTitle: 'In và gửi — tùy chọn',
    deliveryPrintBody: 'Chúng tôi in và gửi cho quý vị bằng màu hoặc đen trắng. Miễn phí kèm theo mỗi gói (màu).',
    twoCostTitle: 'Hai khoản chi phí riêng biệt — không có gì giấu giếm',
    twoCostSub: 'Khoản quý vị trả cho Noor, và khoản quý vị trả cho chính phủ.',
    noorFeeTitle: 'Phí dịch vụ Noor',
    noorFeeDesc: 'Hỗ trợ chuẩn bị mẫu đơn của quý vị',
    govFeeTitle: 'Phí chính phủ Hoa Kỳ',
    govFeeDesc: 'Trả theo mỗi người, trực tiếp cho chính phủ',
    govNoteBefore: 'Phí chính phủ do chính phủ quy định, thay đổi theo loại đơn, và được trả theo mỗi người khi quý vị nộp — Noor không bao giờ thu các khoản này. Một gia đình trả một gói Noor cộng với phí chính phủ riêng của mỗi người. Xem mức phí hiện tại và nộp đơn trực tiếp tại ',
    govLink: 'travel.state.gov',
    govNoteAfter: '.'
  }
};
export function Pricing() {
  const {
    language
  } = useLanguage();
  const t = T[language];
  // Track the selected package by index so it survives a language change.
  const [selected, setSelected] = useState<number>(0);
  return <section id="pricing" className="py-24 bg-white relative z-10" data-id="element-345" data-mp="275jlptwf2imm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-346" data-mp="2289qtyq9dere">
        {/* Heading */}
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-347" data-mp="2fywpjqw0r4nh">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-textPrimary mb-4" data-id="element-348" data-mp="2fyd3bxzc5u3n">
            {t.heading}
          </h2>
          <p className="text-lg text-brand-textSecondary" data-id="element-349" data-mp="2pxc4kvhrungm">{t.headingSub}</p>
        </div>

        {/* Prefer to talk? */}
        <div className="max-w-3xl mx-auto mb-14 bg-brand-bg/50 border border-brand-border rounded-3xl p-6 sm:p-8 text-center" data-id="element-350" data-mp="2kyrcxkgl5c3s">
          <div className="flex items-center justify-center gap-2 mb-2" data-id="element-351" data-mp="3s9x255s4udj6">
            <Phone className="w-5 h-5 text-brand-accent" aria-hidden="true" data-id="element-352" data-mp="6currxs20he" />
            <h3 className="font-heading font-semibold text-xl text-brand-textPrimary" data-id="element-353" data-mp="55z8e2hipiqq">
              {t.talkTitle}
            </h3>
          </div>
          <p className="text-brand-textSecondary mb-5 max-w-xl mx-auto" data-id="element-354" data-mp="a3r4l8p73r0q">
            {t.talkBody}
          </p>
          <ContactButtons size="lg" showNumber className="flex flex-col items-center" data-id="element-355" data-mp="38oae0le9u4x5" />
        </div>

        {/* Packages */}
        <div className="text-center mb-8" data-id="element-356" data-mp="3ijuab8wqz5mf">
          <h3 className="font-heading font-bold text-2xl text-brand-textPrimary" data-id="element-357" data-mp="3iispicpxj4ri">
            {t.assistedTitle}
          </h3>
          <p className="text-brand-textSecondary mt-1" data-id="element-358" data-mp="2pwhl0qz7cjpf">{t.assistedSub}</p>
          <p className="text-sm text-brand-textSecondary mt-2" data-id="element-359" data-mp="2pudlel8c66uy">{t.tapHint}</p>
        </div>

        <div className="max-w-5xl mx-auto bg-white rounded-3xl border border-brand-border shadow-premium overflow-hidden mb-16" data-id="element-360" data-mp="2ut80pd73hdab">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-brand-border" data-id="element-361" data-mp="2icyn7elaulws">
            {t.tiers.map((tier, index) => {
            const isSelected = selected === index;
            return <div key={index} onClick={() => setSelected(index)} role="button" tabIndex={0} aria-pressed={isSelected} aria-label={`${tier.name} — $${tier.price}`} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelected(index);
              }
            }} className={`relative pt-9 pb-6 px-7 sm:pt-10 sm:pb-7 sm:px-8 flex flex-col cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-accent ${isSelected ? 'bg-brand-accent/5' : 'hover:bg-brand-bg/40'}`} data-id="element-362" data-mp="28dpwqvpn7pf6" data-mpi={index}>
                  {tier.popular && <div className="absolute top-2.5 sm:top-3 left-1/2 -translate-x-1/2 bg-brand-accent text-brand-textPrimary px-3 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase" data-id="element-363" data-mp="llhbt0gl5hi">
                      {language === 'es' ? 'Más popular' : language === 'ht' ? 'Pi popilè' : language === 'vi' ? 'Phổ biến nhất' : 'Most Popular'}
                    </div>}
                  {isSelected && <div className="absolute top-4 right-4 w-7 h-7 rounded-full bg-brand-accent flex items-center justify-center shadow-sm" data-id="element-364" data-mp="350lcnm8qfg9d">
                      <Check className="w-4 h-4 text-brand-textPrimary" data-id="element-365" data-mp="2v0uf1bovkl52" />
                    </div>}
                  <div className="text-center mb-3" data-id="element-366" data-mp="1ajbi43b37ggz">
                    <h4 className="text-xl font-bold text-brand-textPrimary" data-id="element-367" data-mp="1aijv3gvk2r2b">
                      {tier.name}
                    </h4>
                    <p className="text-sm text-brand-textSecondary" data-id="element-368" data-mp="1kh97wv8lhlbp">{tier.who}</p>
                    <p className="text-xs font-medium uppercase tracking-wide text-brand-textSecondary mt-1" data-id="element-369" data-mp="1kf50i3bhm46o">
                      {tier.forms}
                    </p>
                  </div>
                  <div className="text-center mb-1" data-id="element-370" data-mp="rqhchzzct0pf">
                    <span className="text-2xl font-bold text-brand-textPrimary align-top" data-id="element-371" data-mp="wsgh1d6ty3ny">
                      $
                    </span>
                    <span className="text-5xl font-bold text-brand-textPrimary tracking-tight" data-id="element-372" data-mp="10r5s2z4eb94f">
                      {tier.price}
                    </span>
                  </div>
                  <p className="text-center text-sm font-semibold text-brand-accent min-h-[20px] mb-1" data-id="element-373" data-mp="91kizybs6c89">
                    {tier.save || ''}
                  </p>
                  <p className="text-center text-xs text-brand-textSecondary min-h-[16px] mb-4" data-id="element-374" data-mp="1gdjvukyzh295">
                    {tier.extra || ''}
                  </p>
                  <Link to={`/pedido?plan=${PLAN_KEYS[index]}`} onClick={e => e.stopPropagation()} className={`mt-auto w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-base transition-all focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 ${isSelected ? 'bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary shadow-sm hover:shadow-md' : 'bg-brand-textPrimary text-white hover:opacity-90'}`} data-id="element-375" data-mp="1bdea1lfea9yz">
                    {t.getStarted}
                    <ArrowRight className="w-4 h-4" data-id="element-376" data-mp="12gn7cq6c7wvf" />
                  </Link>
                </div>;
          })}
          </div>

          {/* Shared list of what every package includes — shown once. */}
          <div className="border-t border-brand-border bg-brand-bg/30 p-5 sm:p-6" data-id="element-377" data-mp="2ld8ce70g97vk">
            <p className="font-semibold text-brand-textPrimary mb-4 text-center" data-id="element-378" data-mp="2lfbehpevmrgn">
              {t.includesTitle}
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-2xl mx-auto" data-id="element-379" data-mp="2bfat7xxg619t">
              {t.includes.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-380" data-mp="2gex3byhzb1ee" data-mpi={i}>
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white border border-brand-border flex items-center justify-center mt-0.5" data-id="element-381" data-mp="343zx24pw0kp9">
                    <Check className="w-4 h-4 text-brand-accent" data-id="element-382" data-mp="3929rue7p9jac" />
                  </div>
                  <span className="text-brand-textPrimary text-sm" data-id="element-383" data-mp="2ud58gtp6590n">{item}</span>
                </li>)}
            </ul>
          </div>
        </div>

        {/* Passport photo add-on — standalone option. Oculta con SHOW_PHOTO=false. */}
        {SHOW_PHOTO && <div className="max-w-3xl mx-auto bg-white border border-brand-border rounded-3xl p-7 sm:p-8 mb-6" data-id="element-384" data-mp="3rtkegj6e79wf">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5" data-id="element-385" data-mp="2460merti3iqe">
              <div className="max-w-md" data-id="element-386" data-mp="1z4uugw7nzzhx">
                <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-1" data-id="element-387" data-mp="1z7z7mj33y5kq">
                  {t.photoTitle}
                </h3>
                <p className="text-brand-textSecondary text-sm" data-id="element-388" data-mp="1p8i1k0jy1po8">{t.photoBody}</p>
              </div>
              <div className="text-center flex-shrink-0" data-id="element-389" data-mp="2mvqgtdtcq2hs">
                <div className="mb-2" data-id="element-390" data-mp="2mxbdb8z87via">
                  <span className="text-xl font-bold text-brand-textPrimary align-top" data-id="element-391" data-mp="2hw43739m2k8g">
                    $
                  </span>
                  <span className="text-4xl font-bold text-brand-textPrimary" data-id="element-392" data-mp="2d42svmw073l2">
                    12
                  </span>
                </div>
                <a href="https://buy.stripe.com/aFaaEXgY94iH01d9IBe7m00" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-base border border-brand-textPrimary text-brand-textPrimary hover:bg-brand-bg transition-colors" data-id="element-393" data-mp="35lorm1oja9p4">
                  {t.photoCta}
                </a>
              </div>
            </div>
          </div>}

        {/* USPS appointment — next step after the photo. Links out to the official
            USPS scheduler. Noor does NOT book appointments (made explicit). */}
        <div className="max-w-3xl mx-auto bg-brand-accent/5 border border-brand-accent/30 rounded-3xl p-7 sm:p-8 mb-6 text-center" data-id="element-394" data-mp="3s0xddhr6mup4">
          <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-2" data-id="element-395" data-mp="4pe8iy470u06">
            {t.uspsTitle}
          </h3>
          <p className="text-brand-textSecondary max-w-xl mx-auto mb-6" data-id="element-396" data-mp="9nfuprwx19fv">
            {t.uspsBody}
          </p>
          <a href="https://tools.usps.com/rcas.htm" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-8 py-4 rounded-full font-semibold text-base transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-397" data-mp="epoogqhm4fj1">
            {t.uspsCta}
            <ArrowRight className="w-4 h-4" data-id="element-398" data-mp="3v28phfouar6i" />
          </a>
          <p className="text-xs text-brand-textSecondary mt-5 max-w-md mx-auto leading-relaxed" data-id="element-399" data-mp="cpajos5d944f">
            {t.uspsNote}
          </p>
        </div>

        {/* Document translation referral — EXTERNAL PARTNER (affiliate).
            Kept GENERIC on purpose (no partner named) so the partner can change
            by only swapping the link. The button points to /contact as a
            placeholder until the real affiliate link is available; when ready,
            replace the <Link to="/contact"> with an external
            <a href={AFFILIATE_URL} target="_blank" rel="noopener noreferrer">.
            Commission is disclosed in transNote (FTC). */}
        <div className="max-w-3xl mx-auto bg-brand-bg/50 border border-brand-border rounded-3xl p-7 sm:p-8 mb-16 text-center" data-id="element-400" data-mp="yy7wbs5m6bju">
          <h3 className="font-heading font-bold text-xl text-brand-textPrimary mb-2" data-id="element-401" data-mp="13yltgf5og133">
            {t.transTitle}
          </h3>
          <p className="text-brand-textSecondary max-w-xl mx-auto mb-6" data-id="element-402" data-mp="u6g2n8r5vpph">
            {t.transBody}
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-brand-textPrimary text-white hover:opacity-90 px-8 py-4 rounded-full font-semibold text-base transition-all shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-403" data-mp="g6yjpa0jr6o1">
            {t.transCta}
            <ArrowRight className="w-4 h-4" data-id="element-404" data-mp="b9ybotk4ztua" />
          </Link>
          <p className="text-xs text-brand-textSecondary mt-5 max-w-md mx-auto leading-relaxed" data-id="element-405" data-mp="20nl2vxhzz8wv">
            {t.transNote}
          </p>
        </div>

        {/* Delivery choice */}
        <div className="max-w-3xl mx-auto mb-16" data-id="element-406" data-mp="se8iwund17zm">
          <p className="text-center font-semibold text-brand-textPrimary mb-4" data-id="element-407" data-mp="sh4fofnh1qmh">
            {t.deliveryTitle}
          </p>
          <div className="grid sm:grid-cols-2 gap-4" data-id="element-408" data-mp="jmgasqlt6ay7">
            <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start gap-3" data-id="element-409" data-mp="jl552l8syt4g">
              <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-410" data-mp="elslc9d8a263">
                <Mail className="w-5 h-5 text-brand-accent" data-id="element-411" data-mp="4ppa8e0eet70" />
              </div>
              <div data-id="element-412" data-mp="4rtkddow4ofq">
                <p className="font-semibold text-brand-textPrimary" data-id="element-413" data-mp="nu9pbqakjua">
                  {t.deliveryEmailTitle}
                </p>
                <p className="text-sm text-brand-textSecondary" data-id="element-414" data-mp="3rvgkdobfhmkn">
                  {t.deliveryEmailBody}
                </p>
              </div>
            </div>
            <div className="bg-white border border-brand-border rounded-2xl p-5 flex items-start gap-3" data-id="element-415" data-mp="1q324eusg2s6v">
              <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-416" data-mp="1xmul7asz5wvp">
                <Printer className="w-5 h-5 text-brand-accent" data-id="element-417" data-mp="22kw3vumhnu6n" />
              </div>
              <div data-id="element-418" data-mp="27cmkujfxlg71">
                <p className="font-semibold text-brand-textPrimary" data-id="element-419" data-mp="27c4gezj47vo1">
                  {t.deliveryPrintTitle}
                </p>
                <p className="text-sm text-brand-textSecondary" data-id="element-420" data-mp="2caxoudjjowhc">
                  {t.deliveryPrintBody}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-cost breakdown */}
        <div className="max-w-2xl mx-auto bg-brand-bg/40 rounded-3xl p-6 sm:p-8 border border-brand-border" data-id="element-421" data-mp="1joms5dz27fpt">
          <h4 className="font-heading font-semibold text-lg text-brand-textPrimary mb-1 text-center" data-id="element-422" data-mp="1sni0avlnyzvr">
            {t.twoCostTitle}
          </h4>
          <p className="text-sm text-brand-textSecondary text-center mb-6" data-id="element-423" data-mp="3009xncayn2j4">
            {t.twoCostSub}
          </p>

          <div className="space-y-3" data-id="element-424" data-mp="340tvyj79sc6u">
            <div className="flex items-start justify-between gap-4 bg-white rounded-xl p-4 border border-brand-border" data-id="element-425" data-mp="3v4q0fefomtdt">
              <div className="flex items-start gap-3 min-w-0" data-id="element-426" data-mp="3yeub3vtf2fm">
                <div className="w-10 h-10 rounded-lg bg-brand-accent/10 flex items-center justify-center flex-shrink-0" data-id="element-427" data-mp="3zzr705lqxgn">
                  <FileText className="w-5 h-5 text-brand-accent" data-id="element-428" data-mp="3qd7xiku3miq7" />
                </div>
                <div className="min-w-0" data-id="element-429" data-mp="3ce8afhm8ibw5">
                  <p className="font-semibold text-brand-textPrimary" data-id="element-430" data-mp="3cd5m9hgaqnjx">
                    {t.noorFeeTitle}
                  </p>
                  <p className="text-sm text-brand-textSecondary" data-id="element-431" data-mp="3hayoy0ie4zfg">
                    {t.noorFeeDesc}
                  </p>
                </div>
              </div>
              <p className="font-heading font-bold text-xl text-brand-textPrimary whitespace-nowrap" data-id="element-432" data-mp="37fn7vbjifg4j">
                $99–$199
              </p>
            </div>

            <div className="flex items-start justify-between gap-4 bg-white rounded-xl p-4 border border-brand-border" data-id="element-433" data-mp="15n8sok8coprt">
              <div className="flex items-start gap-3 min-w-0" data-id="element-434" data-mp="2q4rofpaejoeb">
                <div className="w-10 h-10 rounded-lg bg-brand-secondaryLink/10 flex items-center justify-center flex-shrink-0" data-id="element-435" data-mp="2l6givqr5401w">
                  <Building2 className="w-5 h-5 text-brand-secondaryLink" data-id="element-436" data-mp="2zvmcmz77ky2e" />
                </div>
                <div className="min-w-0" data-id="element-437" data-mp="2v4cpq6d1o8u7">
                  <p className="font-semibold text-brand-textPrimary" data-id="element-438" data-mp="2v3bfg43urqdj">
                    {t.govFeeTitle}
                  </p>
                  <p className="text-sm text-brand-textSecondary" data-id="element-439" data-mp="34ufsgy9yx15q">
                    {t.govFeeDesc}
                  </p>
                </div>
              </div>
              <p className="font-heading font-bold text-xl text-brand-textPrimary whitespace-nowrap" data-id="element-440" data-mp="3itfia997vm3h">
                $130+
              </p>
            </div>
          </div>

          <p className="text-xs text-brand-textSecondary mt-6 leading-relaxed" data-id="element-441" data-mp="1oh8t26jhn4s6">
            {t.govNoteBefore}
            <a href="https://travel.state.gov" target="_blank" rel="noopener noreferrer" className="text-brand-secondaryLink underline hover:text-brand-textPrimary transition-colors" data-id="element-442" data-mp="1jiea94litg82">
              {t.govLink}
            </a>
            {t.govNoteAfter}
          </p>
        </div>
      </div>
    </section>;
}